import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { EyeOff, Loader2, Send, ShieldAlert } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useLicenseActivations } from '@/hooks/supabase/useLicenseActivations';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import { useMarketplacePublishWorkflow, type MarketplaceReviewAction } from '@/hooks/supabase/useMarketplacePublishWorkflow';

interface PublishTabProps {
  product: MapleMarketApp;
  disabled: boolean;
  isSiteAdmin: boolean;
  onSaved: () => Promise<void> | void;
}

function getVisibilityTone(status: MapleMarketApp['visibilityStatus']) {
  if (status === 'published') {
    return 'default';
  }

  return 'secondary';
}

export default function PublishTab({ product, disabled, isSiteAdmin, onSaved }: PublishTabProps) {
  const { toast } = useToast();
  const [rejectionReason, setRejectionReason] = useState(product.publishRejectionReason || '');
  const { primaryLicense, limitReached, activateCurrentDevice } = useLicenseActivations({ autoActivate: false });
  const {
    requestPublish,
    reviewPublishRequest,
    submitting,
    publishGateLoading,
    hasProOrEnterprise,
    currentDeviceActivated,
  } = useMarketplacePublishWorkflow(true);

  const isPending = product.publishRequestStatus === 'pending';
  const showLicenseAlert = !isSiteAdmin && !hasProOrEnterprise;

  const showActivationAlert = !isSiteAdmin && hasProOrEnterprise && !currentDeviceActivated && !publishGateLoading;
  const ownerActionDisabled = disabled || submitting || publishGateLoading || !hasProOrEnterprise || !currentDeviceActivated || isPending;

  const helperText = useMemo(() => {
    if (isSiteAdmin) {
      return 'Site admins can change visibility directly and approve or reject first-time publication requests.';
    }

    if (product.visibilityStatus === 'published') {
      return 'Your product is live. If it is later unpublished or hidden, you can submit it for publication again from this panel.';
    }

    if (product.hasBeenPublished) {
      return 'This product has been approved before. Request Publish will publish it again immediately.';
    }

    return 'First-time publication requires MapleOS Pro access and an admin review.';
  }, [isSiteAdmin, product.hasBeenPublished, product.visibilityStatus]);

  const runAdminAction = async (action: MarketplaceReviewAction, reason?: string) => {
    try {
      const result = await reviewPublishRequest(product.id, action, reason);
      await onSaved();
      toast({
        title: 'Publishing updated',
        description: result?.message || 'The publishing workflow was updated.',
      });
    } catch (error) {
      toast({
        title: 'Publishing action failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleOwnerRequest = async () => {
    try {
      const result = await requestPublish(product.id);
      await onSaved();
      toast({
        title: result?.mode === 'republished' ? 'Product published' : 'Publish request submitted',
        description: result?.message || 'Your request has been processed.',
      });
    } catch (error) {
      toast({
        title: 'Publish request failed',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      {showLicenseAlert && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-950 [&>svg]:text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>MapleOS Pro or Enterprise required</AlertTitle>
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>Owners must use a MapleOS Pro build before requesting publication.</span>
            <Button asChild size="sm" className="w-full sm:w-auto">
              <Link to="/purchase">Upgrade MapleOS</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {showActivationAlert && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-950 [&>svg]:text-amber-700 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>{limitReached ? 'Device limit reached' : 'Activate this device to publish'}</AlertTitle>
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {limitReached
                ? `${primaryLicense?.edition || 'This MapleOS license'} is already active on the maximum number of devices.`
                : 'Activate this MapleOS Pro device before requesting publication.'}
            </span>
            {!limitReached && (
              <Button size="sm" onClick={() => void activateCurrentDevice(true)}>
                Activate this device
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Publishing & Visibility</h3>
            <p className="mt-1 text-sm text-muted-foreground">{helperText}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={getVisibilityTone(product.visibilityStatus)} className="capitalize">
              {product.visibilityStatus}
            </Badge>
            <Badge variant="outline" className="capitalize">
              {product.publishRequestStatus}
            </Badge>
            <Badge variant="secondary">{product.hasBeenPublished ? 'Approved before' : 'Never approved'}</Badge>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Visibility</div>
            <div className="mt-2 text-sm font-medium capitalize text-foreground">{product.visibilityStatus}</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Approval</div>
            <div className="mt-2 text-sm font-medium capitalize text-foreground">{product.publishRequestStatus}</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Requested</div>
            <div className="mt-2 text-sm font-medium text-foreground">{product.publishRequestedAt ? new Date(product.publishRequestedAt).toLocaleString() : 'Not requested'}</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">First published</div>
            <div className="mt-2 text-sm font-medium text-foreground">{product.firstPublishedAt ? new Date(product.firstPublishedAt).toLocaleDateString() : 'Never'}</div>
          </div>
        </div>

        {product.publishRejectionReason && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-medium">Latest rejection reason</div>
            <div className="mt-1 whitespace-pre-line">{product.publishRejectionReason}</div>
          </div>
        )}
      </div>

      {isSiteAdmin ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div>
              <h4 className="font-semibold text-foreground">Admin visibility actions</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Hide products to remove them from discovery, unpublish them to keep them off the public store, or publish them immediately.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" disabled={submitting} onClick={() => void runAdminAction('hide')}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <EyeOff className="mr-2 h-4 w-4" />}
                Hide
              </Button>
              <Button type="button" variant="outline" disabled={submitting} onClick={() => void runAdminAction('unpublish')}>
                Unpublish
              </Button>
              <Button type="button" disabled={submitting} onClick={() => void runAdminAction('publish')}>
                Publish
              </Button>
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div>
              <h4 className="font-semibold text-foreground">Pending approval review</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                First-time publication requests can be approved or rejected here. Rejection requires a correction reason.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" disabled={submitting || !isPending} onClick={() => void runAdminAction('approve')}>
                Approve request
              </Button>
            </div>
            <div className="space-y-2">
              <Textarea
                value={rejectionReason}
                onChange={(event) => setRejectionReason(event.target.value)}
                placeholder="Tell the owner what must be corrected before approval."
                rows={5}
              />
              <Button
                type="button"
                variant="destructive"
                disabled={submitting || !isPending || !rejectionReason.trim()}
                onClick={() => void runAdminAction('reject', rejectionReason.trim())}
              >
                Reject request
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h4 className="font-semibold text-foreground">Owner action</h4>
          <p className="mt-1 text-sm text-muted-foreground">
            Submit this product for first-time review or publish it again after a previous approval.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" disabled={ownerActionDisabled} onClick={() => void handleOwnerRequest()}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {isPending ? 'Publish request pending' : product.hasBeenPublished ? 'Request Publish' : 'Submit for Approval'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
